import React from 'react';

import { buildPropertyReference } from 'src/helpers/tradeHelpers';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_TYPE_COMMUNITY,
  TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED,
} from 'src/util/constants';

/**
 * Build a label for a trade rule meter.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {object} trader
 * @param {object} counterparty
 * @param {string} direction
 * @param {boolean} isPopover
 * @returns {import('react').ReactNode|null} - formatted trade rule meter label with React elements,
 * or null if required data is missing.
 */
export const buildTradeRuleMeterLabel = (
  intl,
  trader,
  counterparty,
  direction,
  isPopover,
) => {
  if (!intl || !trader) {
    return null;
  }
  const { tradePoint } = trader;
  const { uuid: tradePointId, meter } = tradePoint || {};
  const { uuid: counterpartyTradepointId } = counterparty?.tradePoint || {};

  const counterpartyCheck = tradePointId === counterpartyTradepointId;
  const finalCounterparty = counterpartyCheck ? '' : counterparty;

  const { title, identifier } = meter || {};
  const propertyReference = buildPropertyReference(tradePoint, isPopover);
  const {
    title: counterpartyTitle,
    identifier: counterpartyIdentifier,
  } = finalCounterparty?.tradePoint?.meter || {};
  const { tradePoint: counterpartyTradePoint } = finalCounterparty || {};
  const counterpartyPropertyReference = buildPropertyReference(counterpartyTradePoint, isPopover);
  const counterpartyMeterExist = counterpartyTitle && counterpartyIdentifier;

  if (!meter) return null;

  if (!propertyReference) {
    return intl.formatMessage({
      id: 'util.i18n.trade.build_trade_rule_meter_label.no_property.label',
      defaultMessage: '{title} ({identifier})',
    }, {
      identifier,
      title,
    });
  }

  if (!counterpartyPropertyReference) {
    if (propertyReference && !counterpartyMeterExist) {
      return intl.formatMessage({
        id: 'util.i18n.trade.build_trade_rule_meter_label.with_property.label',
        defaultMessage: '{title} ({identifier}) at {propertyReference}',
      }, {
        identifier,
        propertyReference,
        title,
      });
    }
    if (counterpartyMeterExist) {
      if (direction === TRADE_DIRECTION_BUY) {
        return intl.formatMessage({
          id: 'util.i18n.trade.build_trade_rule_meter_label.buy_direction.with_property_and_counterparty_meter.label',
          defaultMessage: '{title} ({identifier}) at {propertyReference} from {counterpartyTitle} ({counterpartyIdentifier})',
        }, {
          counterpartyIdentifier,
          counterpartyTitle,
          identifier,
          propertyReference,
          title,
        });
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return intl.formatMessage({
          id: 'util.i18n.trade.build_trade_rule_meter_label.sell_direction.with_property_and_counterparty_meter.label',
          defaultMessage: '{title} ({identifier}) at {propertyReference} to {counterpartyTitle} ({counterpartyIdentifier})',
        }, {
          counterpartyIdentifier,
          counterpartyTitle,
          identifier,
          propertyReference,
          title,
        });
      }
    }
  }

  if (counterpartyMeterExist) {
    if (direction === TRADE_DIRECTION_BUY) {
      return intl.formatMessage({
        id: 'util.i18n.trade.build_trade_rule_meter_label.buy_direction.with_property_and_counterparty_meter_and_counterparty_property.label',
        defaultMessage: '{title} ({identifier}) at {propertyReference} from {counterpartyTitle} ({counterpartyIdentifier}) at {counterpartyPropertyReference}',
      }, {
        counterpartyIdentifier,
        counterpartyTitle,
        counterpartyPropertyReference,
        identifier,
        propertyReference,
        title,
      });
    }
    if (direction === TRADE_DIRECTION_SELL) {
      return intl.formatMessage({
        id: 'util.i18n.trade.build_trade_rule_meter_label.sell_direction.with_property_and_counterparty_meter_and_counterparty_property.label',
        defaultMessage: '{title} ({identifier}) at {propertyReference} to {counterpartyTitle} ({counterpartyIdentifier}) at {counterpartyPropertyReference}',
      }, {
        counterpartyIdentifier,
        counterpartyTitle,
        counterpartyPropertyReference,
        identifier,
        propertyReference,
        title,
      });
    }
  }

  return null;
};

/**
 * Generates a localized meter label for trade rules based on trade direction.
 * @param {object} intl - React Intl instance for internationalization
 * @param {object} buyer - Trader object representing the buyer (from GraphQL TradeRule.buyer)
 * @param {string} buyer.userId - User ID of the buyer
 * @param {string} buyer.communityId - Community ID of the buyer
 * @param {string} buyer.residualId - Residual ID of the buyer
 * @param {object} buyer.tradePoint - TradePoint object containing meter information
 * @param {object} seller - Trader object representing the seller (from GraphQL TradeRule.seller)
 * @param {string} seller.userId - User ID of the seller
 * @param {string} seller.communityId - Community ID of the seller
 * @param {string} seller.residualId - Residual ID of the seller
 * @param {object} seller.tradePoint - TradePoint object containing meter information
 * @param {string} tradeDirection - Trade direction constant (TRADE_DIRECTION_BUY or
 * TRADE_DIRECTION_SELL)
 * @param {string} tradeType
 * @returns {string} Localized meter label indicating buying or selling direction
 */
export const getTradeRuleMeterLabel = (intl, buyer, seller, tradeDirection, tradeType) => {
  if (!intl || !buyer || !seller || !tradeDirection || !tradeType) return null;

  let meterLabel;

  if (tradeDirection === TRADE_DIRECTION_BUY) {
    if (tradeType === TRADE_TYPE_COMMUNITY || tradeType === TRADE_TYPE_NOMINATED) {
      meterLabel = buildTradeRuleMeterLabel(
        intl,
        buyer,
        seller,
        tradeDirection,
        false,
      );
    }

    if (tradeType === TRADE_TYPE_CONTRACTED) {
      meterLabel = buildTradeRuleMeterLabel(
        intl,
        buyer,
        null,
        null,
        false,
      );
    }

    return intl.formatMessage(
      {
        id: 'util.i18n.trade.get_trade_rule_meter_label.buy.rule.meter',
        defaultMessage: 'Buying for {meterLabel}',
      },
      {
        meterLabel,
      },
    );
  }
  if (tradeDirection === TRADE_DIRECTION_SELL) {
    if (tradeType === TRADE_TYPE_COMMUNITY || tradeType === TRADE_TYPE_NOMINATED) {
      meterLabel = buildTradeRuleMeterLabel(
        intl,
        seller,
        buyer,
        tradeDirection,
        false,
      );
    }
    if (tradeType === TRADE_TYPE_CONTRACTED) {
      meterLabel = buildTradeRuleMeterLabel(
        intl,
        seller,
        null,
        null,
        false,
      );
    }
    return intl.formatMessage(
      {
        id: 'util.i18n.trade.get_trade_rule_meter_label.sell.rule.meter',
        defaultMessage: 'Selling from {meterLabel}',
      },
      {
        meterLabel,
      },
    );
  }
  return meterLabel;
};

/**
 * Build the content that needs to be shown inside the trade rule card for untraded energy.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {string} title -  property title
 * @returns {import('react').ReactNode|null} - trade rule card content.
 */
export const getUntradedContent = (intl, title) => {
  if (!title) {
    return (
      intl.formatMessage({
        id: 'util.i18n.trade.get_untraded_content.no_title.details',
        defaultMessage: 'Untraded energy at this property',
      })

    );
  }

  return (
    intl.formatMessage(
      {
        id: 'util.i18n.trade.get_untraded_content.title.details',
        defaultMessage: 'Untraded energy at {title}',
      },
      { title: <strong>{title}</strong> },
    )

  );
};
